import React, { useEffect } from "react";
import { Typography } from "@mui/material";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend
);

// const labels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul"];

let data1 = {
  labels: [],
  datasets: [
    {
      label: "Estimated",
      data: [10, 20, 30, 40, 50, 50, 50],
      borderColor: "#4087ff",
      backgroundColor: "rgba(64, 135, 255, 0.1)",
      borderWidth: 2.5,
      pointRadius: 4,
      tension: 0.4,
      fill: "origin",
    },
    {
      label: "Actual",
      data: [10, 25, 35, 45, 48, 0, 0],
      borderColor: "#f6b93b",
      borderWidth: 2.5,
      pointRadius: 4,
      pointBackgroundColor: "#f6b93b",
      backgroundColor: "rgba(24, 186, 46, 0.1)",
      fill: "origin",
      tension: 0.4,
    },
  ],
};

let colors = ["#f6b93b", "#4087ff", "#16ca13ff", "#eb3838ff", "#9913c6ff", "#139fcaff"]

const options = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      labels: {
        usePointStyle: true,   // 👈 switch to point style
        pointStyle: 'circle',  // 👈 make it a dot
        boxWidth: 10,          // optional: smaller size
        boxHeight: 10
      }
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: "#8fa5c7", font: { size: 11 } },
      border: { display: false },
    },
    y: {
      min: 10,
      max: 50,
      grid: { display: false },
      ticks: { color: "#8fa5c7", font: { size: 11 } },
      border: { display: false },
    },
  },
};

// const futureBgPlugin = {
//   // id: "futureBg",
//   id: "multiBg",
//   beforeDraw(chart) {
//     const {
//       ctx,
//       chartArea: { left, right, top, bottom },
//       scales: { x },
//     } = chart;
//     const start = x.getPixelForValue("May");
//     ctx.save();
//     ctx.fillStyle = "rgba(215, 231, 238, 0.5)";
//     ctx.fillRect(start, top, right - start, bottom - top);
//     ctx.restore();
//   },
// };

const multiBgPlugin = {
  id: "multiBg",
  beforeDraw(chart) {
    const {
      ctx,
      chartArea: { top, bottom },
      scales: { x },
    } = chart;

    const drawBand = (fromLabel, toLabel, color) => {
      const start = x.getPixelForValue(fromLabel);
      const end = x.getPixelForValue(toLabel);
      ctx.save();
      ctx.fillStyle = color;
      ctx.fillRect(start, top, end - start, bottom - top);
      ctx.restore();
    };

    // Background for Estimated line range (Jan–Apr)
    drawBand("Jan", "Apr", "rgba(64, 135, 255, 0.08)");

    // Background for Actual line range (Feb–May)
    drawBand("Feb", "May", "rgba(246, 185, 59, 0.08)");
  },
};

const formatdate = (date) => date.split("T")[0]

function findStartEndDate(startDateISO, durationDays) {
  const startDate = new Date(startDateISO);

  // ✅ Validate date before using it
  if (!(startDate instanceof Date) || isNaN(startDate)) {
    console.error("Invalid startDateISO:", startDateISO);
    return {
      startDate: null,
      endDate: null,
      formattedStartDate: "",
      formattedEndDate: ""
    };
  }

  // ✅ Ensure duration is a valid number
  const days = Number(durationDays);
  const safeDays = isNaN(days) ? 1 : days;

  // ✅ Add days in UTC
  startDate.setUTCDate(startDate.getUTCDate() + safeDays);

  const endDateISO = startDate.toISOString();

  return {
    startDate: startDateISO,
    endDate: endDateISO,
    formattedStartDate: formatdate(startDateISO),
    formattedEndDate: formatdate(endDateISO),
  };
}


function splitIntoDays(startDateStr, endDateStr, divident = 5) {
  // Parse YYYY-MM-DD safely
  const parseDate = (str) => {
    const [year, month, day] = str.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  const formatDate = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const startDate = parseDate(startDateStr);
  const endDate = parseDate(endDateStr);

  const totalDays = Math.floor(
    (endDate - startDate) / (1000 * 60 * 60 * 24)
  );

  const step = totalDays / 5;

  const equalizedDates = Array.from({ length: 5 }, (_, i) => {
    const d = new Date(startDate);
    d.setDate(startDate.getDate() + Math.round(step * (i + 1)));
    return formatDate(d);
  });

  return {
    totalDays,
    equalizedDates
  };
}

// const result = splitIntoDays("07-01-2026", "17-01-2040",3);

// console.log("Number of days:", result.totalDays);
// console.log("Equalized parts:", result.equalizedDates);


export default function ProjectsChart({ projects = [], progress = [], onChange }) {
  const [project, setProject] = React.useState({})
  const [data, setData] = React.useState(data1)

  useEffect(() => {

    if (progress.length > 0) {
      let prj = projects.filter((p) => p.id == project)[0]
      let days = findStartEndDate(prj?.start_project_date, prj?.duration || 0)
      let labels = splitIntoDays(days.formattedStartDate, days.formattedEndDate).equalizedDates
      let datasets = progress?.map((p, index) => {
        let color = colors[index]
        return {
          label: p.site_name,
          data: p.tasks?.map((t) => Number(t.progress?.value)),
          borderColor: color,
          backgroundColor: color,
          borderWidth: 2.5,
          pointRadius: 4,
          tension: 0.2,
          fill: "origin",
        }
      })
      console.log("datasets=", datasets)
      let updateddata = {
        labels: labels,
        datasets: datasets
      }
      setData(updateddata)
    }

  }, [progress])

  const onChangeEvent = (e) => {
    setProject(e.target.value)
    onChange(e)
  }

  return (
    <div>
      {/* same header and legend as previous example */}
      <div style={{ marginBottom: 30, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography className="dark-gray" sx={{ fontWeight: 500, fontSize: 20, minWidth: 200 }} >Projects Status</Typography>

        <select name="" id="" onChange={onChangeEvent} style={{
          borderRadius: 10, maxWidth: 220, padding: 8,
          fontSize: 12,
          paddingLeft: 10,

        }} className="input-label">
          <option value="">Select Project</option>
          {projects?.map((project, index) => (
            <option key={index} value={project?.id}>{project?.project_name}</option>
          ))}
        </select>

      </div>
      <div
        className='rounded-lg shadow'
        style={{
          padding: 4,
        }}
      >
        <div style={{ height: 'auto', position: "relative" }}>
          <div style={{ overflowX: "auto" }}>
            <div >
              <Line data={data} options={options} plugins={[multiBgPlugin]} />
            </div>
          </div>
        </div>
      </div>
    </div >
  );
}
